import { fetchAuditLogSearches } from 'api/logs/postAuditLogSearches';
import { OptionType } from 'modules/Organizations/components/Search/types';
import { useQuery } from 'react-query';
import { useState } from 'react';
import { ApiAuditLogEntry, ApiAuditLogTarget, V2PostAuditLogSearchesBody } from 'types/swagger/clarifai-api';
import { queryKeys } from 'utils/react-query';
import { useFeaturedFlags } from 'modules/FeaturedFlags/useFeaturedFlags';
import { useHasUserLevelScopes } from 'modules/Auth/hooks';
import { permissionMatchers } from 'utils/scopes/permissionMatchers';
import { ALL_APPS_OPTION } from './AllAppsDropdown';

export type AuditAPIDataType = Pick<
  ApiAuditLogEntry,
  'timestamp' | 'user' | 'operation' | 'description' | 'details' | 'success' | 'source_ip' | 'targets'
>;

const convertToTableData = (data: ApiAuditLogEntry[]) => {
  return data?.map((entry: ApiAuditLogEntry) => {
    return {
      timestamp: entry.timestamp,
      userName: `${entry.user?.first_name} ${entry.user?.last_name}`,
      user: entry.user,
      operation: entry.operation,
      description: entry.description,
      details: entry.details,
      success: entry.success,
      status: entry.success ? 'Success' : 'Failed',
      clientIP: entry.source_ip,
      targets: entry.targets,
    } as AuditAPIDataType;
  });
};

export function useHasAuditLogAccess() {
  const [hasFeatureFlag] = useFeaturedFlags(['AuditLogging']);
  const hasScopes = useHasUserLevelScopes([permissionMatchers.auditLog.list]);
  return hasScopes && hasFeatureFlag;
}

export function useAuditLogData({
  userId,
  timestamp_from,
  timestamp_to,
  targetApps,
}: {
  userId: string;
  timestamp_from: string;
  timestamp_to: string;
  targetApps: OptionType<Record<string, unknown>>[] | null;
}) {
  const enabled = useHasAuditLogAccess();
  const [apiError, setApiError] = useState<CF.API.ApiError | null>(null);

  /* 
    Intitially targetApps is null, means user has not selected any app. So show all the records.
    BE will return all the records if targetApps is empty or full list of apps.
   */
  const showRecord = targetApps === null || targetApps?.length > 0;
  const targets =
    targetApps?.[0]?.value === ALL_APPS_OPTION.value ? [] : (targetApps?.map((app) => ({ app: { id: app.value } })) as ApiAuditLogTarget[]);

  const body: V2PostAuditLogSearchesBody = {
    query: { timestamp_from, timestamp_to, targets: targets as ApiAuditLogTarget[] },
  };
  const { data, isLoading } = useQuery(
    [queryKeys.AuditLogSearch, userId, body.query?.timestamp_from, body.query?.timestamp_to, body.query?.targets],
    () => fetchAuditLogSearches({ userId, body }),
    {
      enabled,
      onSuccess: () => {
        setApiError(null);
      },
      onError: (error: CF.API.ApiError) => {
        setApiError(error);
        console.error(error?.status?.description || 'Failed to fetch audit logs');
      },
    },
  );
  return {
    data: showRecord ? convertToTableData(data?.entries as unknown as ApiAuditLogEntry[]) : [],
    isLoading,
    error: apiError,
  };
}
